import React, { useEffect } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import has from 'lodash/has';
import get from 'lodash/get';
import FormInputStyleProperties from '@ui/form/FormInputStyleProperties';
import { useTranslation } from '@ui/i18n/Translation';
import _uniqueId from 'lodash/uniqueId';
import Utilities from '@ui/utils/Utilities';

type RadioValue = string | number | null | undefined;

type Props = {
	isRequired?: boolean,
	placeholder?: string,
	form?: any,
	name: string,
	label?: string,
	labelHint?: React.JSX.Element,
	disabled?: boolean,
	labelClassName?: string,
	hideErrorMessage?: boolean,
	options: { label: string, value: RadioValue}[],
	onChange?: (val: RadioValue) => any
} & FormInputStyleProperties

const FormRadio: React.FunctionComponent<Props> = (
	{
		form,
		placeholder,
		name,
		isRequired = false,
		label,
		isLoading,
		disabled = false,
		labelClassName,
		labelHint,
		hideErrorMessage = false,
		options,
		onChange,
	}) => {

	const { t } = useTranslation('common.form.error');
	const hasError = !!form && has(form.formState.errors, name);
	const errorMessage = hasError ? (get(form.formState.errors, name + '.message') || t('required')) : '';

	const validationOptions: Partial<RegisterOptions> = {
		required: isRequired,
	};

	const uniqueId = _uniqueId('radio-');

	useEffect(() => {
		if (!!form && isRequired && !form.getValues(name)) {
			form.setValue(name, options[0].value);
			if (onChange) onChange(options[0].value);
		}
	}, [form]);

	useEffect(() => {
		if (disabled)
			form.setValue(name, undefined);
	}, [disabled]);

	const renderField = () => {
		return <>
			{!!form &&
				<div className="flex-wrap space-y-4 sm:flex sm:items-center sm:space-x-4 sm:space-y-0">
					{renderOptions()}
				</div>
			}
		</>;
	};

	const renderOptions = () => {
		const defaultValue = form.getValues(name) === undefined ? options[0]?.value : form.getValues(name);

		return <Controller
			control={form.control}
			name={name}
			defaultValue={defaultValue}
			rules={validationOptions}
			render={({ field }) => <>
				{options.map((o, idx) => <div key={'option_' + String(o.value)} className="flex items-center">
					<input id={uniqueId + '_option_' + o.value}
								 name={field.name}
								 disabled={disabled}
								 value={String(o.value ?? '')}
								 type="radio"
								 ref={idx === 0 ? field.ref : undefined}
								 checked={field.value === o.value}
								 onBlur={field.onBlur}
								 onChange={() => {
									 field.onChange(o.value);
									 if (onChange) onChange(o.value);
								 }}
								 className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-600" />
					<label htmlFor={uniqueId + '_option_' + o.value}
								 className={Utilities.classNames(labelClassName ? labelClassName : 'ml-2 block text-sm font-medium leading-6 whitespace-nowrap', disabled ? 'text-gray-400' : 'text-gray-500')}>{o.label}</label>
				</div>)}
			</>} />;
	};

	return <>
		{!!label && <div className={Utilities.classNames('label')}>{label}</div>}
		{!!labelHint ? labelHint : ''}
		{renderField()}
		{!!errorMessage && !hideErrorMessage && <p className="help help-error">{errorMessage}</p>}
	</>;

};

export default FormRadio;
